import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { participantsService, nationsService, ageGroupsService, pdfExportService } from "../../services";
import { useLocation } from "react-router-dom";
import AdminModalTailwind from "./AdminModalTailwind";
import ParticipantFormTailwind from "./ParticipantFormTailwind";
import ConfirmationModal from "./ConfirmationModal";
import PartnerTeamsWizard from "./PartnerTeamsWizard";
import GenericSelectTailwind from "./GenericSelectTailwind";
import FlagSelectTailwind from "./FlagSelectTailwind";
import Toast from "./Toast";
import { getAgeGroupColors } from "../../utils/ageGroupColors";

function ParticipantsTailwind() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedNationality, setSelectedNationality] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [participants, setParticipants] = useState([]);
  const [nations, setNations] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [teamMembersSet, setTeamMembersSet] = useState(new Set()); // Set von Participant IDs, die im Team sind
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const participantFormRef = useRef();
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Multi-Select States
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  
  // Confirmation Modal States
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  
  // Groups Modal States
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [selectedParticipantGroups, setSelectedParticipantGroups] = useState([]);
  const [selectedParticipantForGroups, setSelectedParticipantForGroups] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  
  // Partner Teams Wizard States
  const [showPartnerTeamsWizard, setShowPartnerTeamsWizard] = useState(false);
  
  // Export States
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  const location = useLocation();

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  // Setze Nationenfilter aus Query-Parameter beim ersten Laden
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nationParam = params.get("nation");
    if (nationParam) {
      setSelectedNationality(nationParam);
    }
  }, [location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Teilnehmer, Nationen und Altersgruppen parallel
      const [participantsResponse, nationsResponse, ageGroupsResponse] = await Promise.all([
        participantsService.getAllParticipants(),
        nationsService.getAllNations(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (participantsResponse.success) {
        setParticipants(participantsResponse.data);
      } else {
        setError(participantsResponse.error);
        addToast('error', participantsResponse.error);
      }

      if (nationsResponse.success) {
        setNations(nationsResponse.data);
      } else {
        setError(nationsResponse.error);
        addToast('error', nationsResponse.error);
      }

      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      } else {
        setError(ageGroupsResponse.error);
        addToast('error', ageGroupsResponse.error);
      }

      // Lade Team-Mitglieder für alle Nationen mit aktivierter Team-Teilnahme
      await loadTeamMembers();
    } catch (err) {
      const errorMessage = "Fehler beim Laden der Daten";
      setError(errorMessage);
      addToast('error', errorMessage);
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Lade Team-Mitglieder für alle Nationen
  const loadTeamMembers = async () => {
    try {
      // Hole alle Nationen mit aktivierter Team-Teilnahme
      const allNations = await nationsService.getAllNations();
      if (!allNations.success) return;

      const nationsWithTeam = allNations.data.filter(n => n.participatesInTeamCompetition || n.participates_in_team_competition);
      
      // Lade Teams für alle Nationen parallel
      const teamPromises = nationsWithTeam.map(nation => 
        nationsService.getNationTeam(nation.id).catch(() => ({ success: false, data: null }))
      );
      
      const teamResults = await Promise.all(teamPromises);
      
      // Erstelle Set von Participant IDs, die im Team sind
      const teamMemberIds = new Set();
      teamResults.forEach(result => {
        if (result.success && result.data?.teamMembers) {
          result.data.teamMembers.forEach(tm => {
            if (tm.participant?.id) {
              teamMemberIds.add(tm.participant.id);
            }
          });
        }
      });
      
      setTeamMembersSet(teamMemberIds);
    } catch (err) {
      console.error("Fehler beim Laden der Team-Mitglieder:", err);
      // Fehler beim Laden der Teams ist nicht kritisch, setze leeres Set
      setTeamMembersSet(new Set());
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleNationalityChange = (nationality) => {
    setSelectedNationality(nationality);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedFilter("all");
    setSelectedAgeGroup("all");
    setSelectedNationality("all");
    setSelectedGender("all");
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedFilter !== "all") {
      const filterLabel = filterOptions.find(f => f.value === selectedFilter)?.label || selectedFilter;
      active.push({ type: "Status", value: filterLabel, onRemove: () => setSelectedFilter("all") });
    }
    if (selectedAgeGroup !== "all") {
      const ageGroupLabel = ageGroupOptions.find(a => a.value === selectedAgeGroup)?.label || selectedAgeGroup;
      active.push({ type: "Altersgruppe", value: ageGroupLabel, onRemove: () => setSelectedAgeGroup("all") });
    }
    if (selectedGender !== "all") {
      const genderLabel = genderOptions.find(g => g.value === selectedGender)?.label || selectedGender;
      active.push({ type: "Geschlecht", value: genderLabel, onRemove: () => setSelectedGender("all") });
    }
    if (selectedNationality !== "all") {
      const nation = nations.find(n => n.code === selectedNationality);
      const nationLabel = nation ? nation.name : selectedNationality;
      active.push({ type: "Nationalität", value: nationLabel, onRemove: () => setSelectedNationality("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  // Modal und Form Handler
  const handleAddParticipant = () => {
    setEditingParticipant(null);
    setShowModal(true);
    setIsFormValid(false);
  };

  const handleEditParticipant = (participant) => {
    setEditingParticipant(participant);
    setShowModal(true);
    // Wait a bit for form to initialize, then check validity
    setTimeout(() => {
      if (participantFormRef.current?.isFormValid) {
        const valid = participantFormRef.current.isFormValid();
        setIsFormValid(valid);
      }
    }, 200);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingParticipant(null);
    setIsFormValid(false);
  };

  // Check form validity periodically when modal is open
  useEffect(() => {
    if (!showModal) return;
    
    const interval = setInterval(() => {
      if (participantFormRef.current?.isFormValid) {
        const valid = participantFormRef.current.isFormValid();
        setIsFormValid(valid);
      }
    }, 100); // Check every 100ms

    return () => clearInterval(interval);
  }, [showModal]);

  const handleSaveParticipant = async (formData) => {
    setIsSaving(true);
    try {
      let response;
      
      if (editingParticipant) {
        // Teilnehmer bearbeiten
        response = await participantsService.updateParticipant(editingParticipant.id, formData);
      } else {
        // Neuen Teilnehmer hinzufügen
        response = await participantsService.addParticipant(formData);
      }

      if (response.success) {
        // Daten neu laden
        await loadData();
        handleCloseModal();
        addToast('success', response.message || `Teilnehmer "${formData.name}" erfolgreich ${editingParticipant ? 'aktualisiert' : 'hinzugefügt'}`);
      } else {
        addToast('error', response.error || 'Fehler beim Speichern des Teilnehmers');
      }
    } catch (err) {
      addToast('error', 'Fehler beim Speichern des Teilnehmers');
      console.error("Fehler beim Speichern:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteParticipant = (participant) => {
    setParticipantToDelete(participant);
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!participantToDelete) return;

    try {
      // Bulk delete
      if (participantToDelete.ids && Array.isArray(participantToDelete.ids)) {
        const deletePromises = participantToDelete.ids.map(id => 
          participantsService.deleteParticipant(id)
        );
        const results = await Promise.all(deletePromises);
        const successCount = results.filter(r => r.success).length;
        
        await loadData();
        setSelectedParticipants(new Set());
        addToast('success', `${successCount} Teilnehmer erfolgreich gelöscht`);
      } else {
        // Single delete
        const response = await participantsService.deleteParticipant(participantToDelete.id);
        
        if (response.success) {
          await loadData();
          addToast('success', `Teilnehmer "${participantToDelete.name}" erfolgreich gelöscht`);
        } else {
          addToast('error', response.error || 'Fehler beim Löschen des Teilnehmers');
        }
      }
    } catch (err) {
      addToast('error', 'Fehler beim Löschen des Teilnehmers');
      console.error("Fehler beim Löschen:", err);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setParticipantToDelete(null);
  };

  // Multi-Select Handlers
  const handleToggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedParticipants(new Set());
  };

  const handleToggleParticipant = (participantId) => {
    setSelectedParticipants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(participantId)) {
        newSet.delete(participantId);
      } else {
        newSet.add(participantId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedParticipants.size === paginatedParticipants.length) {
      setSelectedParticipants(new Set());
    } else {
      setSelectedParticipants(new Set(paginatedParticipants.map(p => p.id)));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedParticipants.size === 0) return;
    
    setIsSaving(true);
    try {
      const participantIds = Array.from(selectedParticipants);
      const updates = participantIds.map(async (id) => {
        const participant = participants.find(p => p.id === id);
        if (!participant) return null;
        
        const response = await participantsService.updateParticipant(id, {
          name: participant.name,
          age: participant.age,
          gender: participant.gender,
          status: newStatus,
          nationId: participant.nation?.id || participant.nationId,
          categoryIds: (participant.categories || participant.category || []).map(cat => cat.id || cat),
          openFormName: participant.openFormName
        });
        return response;
      });

      await Promise.all(updates);
      await loadData();
      setSelectedParticipants(new Set());
      addToast('success', `${participantIds.length} Teilnehmer erfolgreich aktualisiert`);
    } catch (err) {
      addToast('error', 'Fehler beim Aktualisieren der Teilnehmer');
      console.error("Fehler beim Bulk-Update:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedParticipants.size === 0) return;
    const selectedArray = Array.from(selectedParticipants);
    setParticipantToDelete({ 
      ids: selectedArray,
      names: selectedArray.map(id => participants.find(p => p.id === id)?.name).filter(Boolean)
    });
    setShowConfirmation(true);
  };

  // Groups Modal Handler
  const handleViewGroups = async (participant) => {
    setGroupsLoading(true);
    setShowGroupsModal(true);
    setSelectedParticipantForGroups(participant);
    
    try {
      // Lade Gruppen für den Teilnehmer
      const response = await participantsService.getParticipantGroups(participant.id);
      
      if (response.success) {
        setSelectedParticipantGroups(response.data || []);
      } else {
        setSelectedParticipantGroups([]);
        addToast('error', response.error || 'Fehler beim Laden der Gruppen');
      }
    } catch (error) {
      setSelectedParticipantGroups([]);
      addToast('error', 'Fehler beim Laden der Gruppen');
      console.error("Fehler beim Laden der Gruppen:", error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleCloseGroupsModal = () => {
    setShowGroupsModal(false);
    setSelectedParticipantGroups([]);
  };

  // Toast Handler
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      // Erstelle Filter-Objekt basierend auf aktuellen Filtern
      const filters = {
        status: selectedFilter,
        ageGroup: selectedAgeGroup,
        nationality: selectedNationality,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportParticipantsPDF(filters);
      
      if (result.success) {
        addToast('success', 'PDF erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim PDF-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim PDF-Export');
      console.error("Fehler beim PDF-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      // Erstelle Filter-Objekt basierend auf aktuellen Filtern
      const filters = {
        status: selectedFilter,
        ageGroup: selectedAgeGroup,
        nationality: selectedNationality,
        gender: selectedGender,
        searchTerm: searchTerm
      };

      const result = await pdfExportService.exportParticipantsJSON(filters);
      
      if (result.success) {
        addToast('success', 'JSON erfolgreich heruntergeladen');
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Export');
      }
    } catch (error) {
      addToast('error', 'Fehler beim JSON-Export');
      console.error("Fehler beim JSON-Export:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import Handler
  const handleImportJSON = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      addToast('error', 'Bitte wählen Sie eine JSON-Datei aus');
      return;
    }

    setIsImporting(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      // Unterstütze beide Formate: direktes Array oder { data: [...] } oder { participants: [...] }
      let participantsArray = [];
      if (Array.isArray(jsonData)) {
        participantsArray = jsonData;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        participantsArray = jsonData.data;
      } else if (jsonData.participants && Array.isArray(jsonData.participants)) {
        participantsArray = jsonData.participants;
      } else {
        throw new Error('Ungültiges JSON-Format. Erwartet wird ein Array oder ein Objekt mit "data" oder "participants"');
      }

      const result = await pdfExportService.importParticipantsJSON(participantsArray);
      
      if (result.success) {
        let message = result.message || 'JSON erfolgreich importiert';
        if (result.data) {
          message = `${message} (${result.data.created || 0} erstellt, ${result.data.updated || 0} aktualisiert${result.data.errors > 0 ? `, ${result.data.errors} Fehler` : ''})`;
        }
        addToast('success', message);
        // Daten neu laden
        await loadData();
      } else {
        addToast('error', result.error || 'Fehler beim JSON-Import');
      }
    } catch (error) {
      addToast('error', 'Fehler beim Importieren der JSON-Datei: ' + error.message);
      console.error("Fehler beim JSON-Import:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const filterOptions = [
    { value: "all", label: t("admin.participants.filters.all", "Alle") },
    { value: "confirmed", label: t("admin.participants.filters.confirmed", "Bestätigt") },
    { value: "pending", label: t("admin.participants.filters.pending", "Ausstehend") }
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.participants.ageGroups.all", "Alle Altersgruppen") },
    ...ageGroups
      .filter((ageGroup) => ageGroup.name !== 'Alle') // "Alle" ausschließen
      .map((ageGroup) => ({
        value: ageGroup.name,
        label: ageGroup.name
      }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.participants.gender.all", "Alle Geschlechter") },
    { value: "männlich", label: t("admin.participants.gender.male", "Männlich") },
    { value: "weiblich", label: t("admin.participants.gender.female", "Weiblich") }
  ];

  // Filter- und Suchlogik
  const filteredParticipants = participants.filter((participant) => {
    // Suche nach Name (case-insensitive)
    const matchesSearch = participant.name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status-Filter
    const matchesStatus =
      selectedFilter === "all" ||
      (selectedFilter === "confirmed" && participant.status?.toLowerCase() === "bestätigt") ||
      (selectedFilter === "pending" && participant.status?.toLowerCase() === "ausstehend");

    // Altersgruppen-Filter
    const matchesAgeGroup =
      selectedAgeGroup === "all" || (participant.ageGroup?.name || participant.ageGroup) === selectedAgeGroup;

    // Nationalitäten-Filter
    const matchesNationality =
      selectedNationality === "all" || (participant.nation?.code || participant.nationCode) === selectedNationality;

    // Geschlechts-Filter
    const matchesGender =
      selectedGender === "all" || participant.gender === selectedGender;

    return matchesSearch && matchesStatus && matchesAgeGroup && matchesNationality && matchesGender;
  });

  // KPI Berechnungen
  const calculateKPIs = () => {
    if (filteredParticipants.length === 0) {
      return {
        totalCount: 0,
        averageAge: 0,
        averageCategories: 0
      };
    }

    // Teilnehmeranzahl
    const totalCount = filteredParticipants.length;

    // Altersschnitt
    const ages = filteredParticipants
      .map(p => p.age)
      .filter(age => age != null && !isNaN(age));
    const averageAge = ages.length > 0
      ? Math.round((ages.reduce((sum, age) => sum + age, 0) / ages.length) * 10) / 10
      : 0;

    // Durchschnittliche Kategorieanzahl pro Athlet
    const categoryCounts = filteredParticipants.map(p => {
      const categories = p.categories || p.category || [];
      return categories.length;
    });
    const averageCategories = categoryCounts.length > 0
      ? Math.round((categoryCounts.reduce((sum, count) => sum + count, 0) / categoryCounts.length) * 10) / 10
      : 0;

    return {
      totalCount,
      averageAge,
      averageCategories
    };
  };

  const kpis = calculateKPIs();

  // Pagination Logic
  const totalItems = filteredParticipants.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedParticipants = itemsPerPage === "all" 
    ? filteredParticipants 
    : filteredParticipants.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter, selectedAgeGroup, selectedNationality, selectedGender, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);


  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Schließe Export-Menü beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.relative')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
              <div className="h-6 w-12 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-72"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="mb-6 flex items-center justify-between gap-3 animate-pulse">
            <div className="flex items-center gap-2">
              <div className="w-36 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="w-32 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-28 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="w-px h-5 bg-slate-200 dark:bg-slate-700"></div>
              <div className="w-24 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="w-20 h-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            {/* Search Bar Skeleton */}
            <div className="mb-6">
              <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>

            {/* Filter Dropdowns Skeleton - 4 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-2"></div>
                  <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(9)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Pagination Footer Skeleton */}
                <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                  <tr>
                    <td colSpan={9} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12"></div>
                          <div className="h-9 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                          <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          <div className="flex items-center gap-2">
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                            ))}
                            <div className="h-9 w-9 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.participants.title", "Teilnehmerverwaltung")}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                  <i className="fas fa-users text-[10px]"></i>
                  {filteredParticipants.length}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 mt-1.5 text-sm">
                {t("admin.participants.subtitle", "Verwalte alle Teilnehmer der Veranstaltung.")}
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          {/* Linke Seite: Sekundär-Aktionen */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleSelectMode}
              className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl border transition-colors ${
                isSelectMode
                  ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30 hover:bg-primary-dark dark:hover:bg-primary'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <i className={`fas ${isSelectMode ? 'fa-check-square' : 'fa-square'} text-xs`}></i>
              {isSelectMode ? 'Auswahl beenden' : 'Mehrfachauswahl'}
            </button>
            <button
              onClick={() => setShowPartnerTeamsWizard(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              <i className="fas fa-handshake text-xs"></i>
              Partner-Teams
            </button>
          </div>

          {/* Rechte Seite: Primäre + Tool-Aktionen */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddParticipant}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.participants.add", "Hinzufügen")}
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700"></div>

            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} text-xs`}></i>
                {isExporting ? 'Export...' : 'Export'}
                {!isExporting && <i className="fas fa-chevron-down text-[10px] opacity-60"></i>}
              </button>
              {showExportMenu && !isExporting && (
                <div className="absolute right-0 mt-1.5 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-10 overflow-hidden">
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5"
                  >
                    <i className="fas fa-file-pdf text-red-500 text-xs w-3.5 text-center"></i>
                    Als PDF exportieren
                  </button>
                  <button
                    onClick={handleExportJSON}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 border-t border-slate-100 dark:border-slate-700/50"
                  >
                    <i className="fas fa-file-code text-blue-500 text-xs w-3.5 text-center"></i>
                    Als JSON exportieren
                  </button>
                </div>
              )}
            </div>

            <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer">
              <i className={`fas ${isImporting ? 'fa-spinner fa-spin' : 'fa-upload'} text-xs`}></i>
              {isImporting ? 'Import...' : 'Import'}
              <input
                type="file"
                accept=".json"
                onChange={handleImportJSON}
                disabled={isImporting}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Filter Section */}
        <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 mb-6">
          {/* Search Bar */}
          <div className="mb-5">
            <div className="relative">
              <input
                type="text"
                placeholder={t("admin.participants.search", "Teilnehmer suchen…")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xs"></i>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {/* Status Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.participants.filters.title", "Status")}
              </label>
              <GenericSelectTailwind
                value={selectedFilter}
                onChange={handleFilterChange}
                options={filterOptions.map(opt => ({
                  value: opt.value,
                  label: opt.label
                }))}
                placeholder="Alle Status"
              />
            </div>

            {/* Age Group Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.participants.ageGroups.title", "Altersgruppe")}
              </label>
              <GenericSelectTailwind
                value={selectedAgeGroup}
                onChange={handleAgeGroupChange}
                options={ageGroupOptions.map(opt => ({
                  value: opt.value,
                  label: opt.label
                }))}
                placeholder="Alle Altersgruppen"
              />
            </div>

            {/* Gender Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.participants.gender.title", "Geschlecht")}
              </label>
              <GenericSelectTailwind
                value={selectedGender}
                onChange={handleGenderChange}
                options={genderOptions.map(opt => ({
                  value: opt.value,
                  label: opt.label,
                  icon: opt.value === "männlich" ? <i className="fas fa-mars text-blue-600"></i> : opt.value === "weiblich" ? <i className="fas fa-venus text-pink-600"></i> : null
                }))}
                placeholder="Alle Geschlechter"
                leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500"></i>}
              />
            </div>

            {/* Nationality Filter */}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                {t("admin.participants.nationality.title", "Nationalität")}
              </label>
              <FlagSelectTailwind
                value={selectedNationality === "all" ? "" : selectedNationality}
                onChange={(value) => handleNationalityChange(value || "all")}
                options={nations}
                allOptionLabel="Alle"
              />
            </div>
          </div>

          {/* Active Filters Section */}
          {getActiveFilters().length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-1">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Aktive Filter
                </span>
                <button
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                >
                  <i className="fas fa-undo text-[10px]"></i>
                  Zurücksetzen
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getActiveFilters().map((filter, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                  >
                    {filter.type}: {filter.value}
                    <button onClick={filter.onRemove} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                      <i className="fas fa-times text-[9px]"></i>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Teilnehmeranzahl */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Teilnehmeranzahl
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.totalCount}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <i className="fas fa-users text-lg"></i>
            </div>
          </div>

          {/* Altersschnitt */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Altersschnitt
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.averageAge > 0 ? kpis.averageAge.toFixed(1) : '0.0'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <i className="fas fa-chart-bar text-lg"></i>
            </div>
          </div>

          {/* Durchschnittliche Kategorieanzahl */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Ø Kategorien pro Athlet
              </p>
              <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                {kpis.averageCategories > 0 ? kpis.averageCategories.toFixed(1) : '0.0'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
              <i className="fas fa-tags text-lg"></i>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {isSelectMode && selectedParticipants.size > 0 && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 border-l-4 border-l-primary dark:border-l-primary-light rounded-xl p-3.5 mb-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-primary/10 dark:bg-primary-light/10 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-check-square text-primary dark:text-primary-light text-xs"></i>
              </div>
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {selectedParticipants.size} ausgewählt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkStatusChange("Bestätigt")}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium rounded-lg border border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-check-circle text-[10px]"></i>
                Bestätigen
              </button>
              <button
                onClick={() => handleBulkStatusChange("Ausstehend")}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-lg border border-amber-200 dark:border-amber-700/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-clock text-[10px]"></i>
                Ausstehend
              </button>
              <div className="h-4 w-px bg-slate-200 dark:bg-slate-600"></div>
              <button
                onClick={handleBulkDelete}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-medium rounded-lg border border-red-200 dark:border-red-700/40 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-trash text-[10px]"></i>
                Löschen
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {filteredParticipants.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  {isSelectMode && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedParticipants.size === paginatedParticipants.length && paginatedParticipants.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary/20 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.id", "ID")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.name", "Name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.nation", "Nation")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.age", "Alter")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.gender", "Geschlecht")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.ageGroup", "Altersgruppe")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-64">
                    {t("admin.participants.table.category", "Kategorien")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.status", "Status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.participants.table.actions", "Aktionen")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {paginatedParticipants.map((participant) => (
                  <tr key={participant.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors ${selectedParticipants.has(participant.id) ? 'bg-primary/5 dark:bg-primary-light/10' : ''}`}>
                      {isSelectMode && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedParticipants.has(participant.id)}
                            onChange={() => handleToggleParticipant(participant.id)}
                            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary/20 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                      {participant.participantNumber || participant.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {participant.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (participant.nation?.code || participant.nationCode) + ".png"}
                          alt={participant.nation?.name || participant.nation}
                          className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                        />
                        <span className="text-sm text-slate-900 dark:text-slate-100">
                          {participant.nation?.name || participant.nation}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                      {participant.age}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const isMale = participant.gender === 'männlich';
                        const isFemale = participant.gender === 'weiblich';
                        const isMixed = !isMale && !isFemale;
                        const label = isMale ? 'Männlich' : isFemale ? 'Weiblich' : participant.gender || 'Gemischt';
                        const iconClass = isMixed
                          ? 'fa-venus-mars text-purple-500'
                          : isMale
                            ? 'fa-mars text-blue-500'
                            : 'fa-venus text-pink-500';
                        return (
                          <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
                            <i className={`fas ${iconClass} text-xs`} />
                            <span>{label}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {participant.ageGroup?.name || participant.ageGroup ? (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          (() => {
                            const colors = getAgeGroupColors(participant.ageGroup?.name || participant.ageGroup);
                            // Map bgLight to dark mode equivalents with border
                            const darkBgMap = {
                              'bg-emerald-100': 'dark:bg-emerald-900/30',
                              'bg-blue-100': 'dark:bg-blue-900/30',
                              'bg-violet-100': 'dark:bg-violet-900/30',
                              'bg-amber-100': 'dark:bg-amber-900/30',
                              'bg-red-100': 'dark:bg-red-900/30',
                              'bg-gray-100': 'dark:bg-gray-700'
                            };
                            // Map text to dark mode equivalents
                            const darkTextMap = {
                              'text-emerald-700': 'dark:text-emerald-300',
                              'text-blue-700': 'dark:text-blue-300',
                              'text-violet-700': 'dark:text-violet-300',
                              'text-amber-700': 'dark:text-amber-300',
                              'text-red-700': 'dark:text-red-300',
                              'text-gray-800': 'dark:text-gray-300'
                            };
                            // Map border colors
                            const borderMap = {
                              'bg-emerald-100': 'border-emerald-700/30 dark:border-emerald-300/30',
                              'bg-blue-100': 'border-blue-700/30 dark:border-blue-300/30',
                              'bg-violet-100': 'border-violet-700/30 dark:border-violet-300/30',
                              'bg-amber-100': 'border-amber-700/30 dark:border-amber-300/30',
                              'bg-red-100': 'border-red-700/30 dark:border-red-300/30',
                              'bg-gray-100': 'border-gray-600/30 dark:border-gray-400/30'
                            };
                            return `${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-700'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-300'} border ${borderMap[colors.bgLight] || 'border-slate-600/30 dark:border-slate-400/30'}`;
                          })()
                        }`}>
                          {participant.ageGroup?.name || participant.ageGroup}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 w-64">
                      <div className="flex flex-wrap gap-1">
                        {(participant.categories || participant.category || []).map((cat, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${
                              cat.type === 'Partnerform'
                                ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700/40'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600/50'
                            }`}
                            title={cat.type === 'Partnerform' ? 'Partnerkategorie' : undefined}
                          >
                            {cat.type === 'Partnerform' && <i className="fas fa-handshake text-[9px]"></i>}
                            {cat.type === 'Offene Form' && (cat.formName || '').trim()
                              ? `${cat.name} – ${cat.formName}`
                              : (cat.name || cat)
                            }
                          </span>
                        ))}
                        {teamMembersSet.has(participant.id) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700/40" title="Im Team der Nation für den Gruppenwettbewerb">
                            <i className="fas fa-users text-[9px]"></i>
                            Gruppenw.
                          </span>
                        )}
                        {(participant.categories || participant.category || []).length === 0 && !teamMembersSet.has(participant.id) && (
                          <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        participant.status?.toLowerCase() === 'bestätigt' 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-700/30 dark:border-emerald-300/30'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-700/30 dark:border-yellow-300/30'
                      }`}>
                        {participant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditParticipant(participant)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                          title={t("admin.participants.actions.edit", "Bearbeiten")}
                        >
                          <i className="fas fa-edit text-[11px]"></i>
                        </button>
                        <button
                          onClick={() => handleViewGroups(participant)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                          title={t("admin.participants.actions.viewGroups", "Gruppen ansehen")}
                        >
                          <i className="fas fa-layer-group text-[11px]"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteParticipant(participant)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-700/50 transition-colors"
                          title={t("admin.participants.actions.delete", "Löschen")}
                        >
                          <i className="fas fa-trash text-[11px]"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Pagination Footer Row */}
                <tr className="bg-slate-50 dark:bg-slate-700/30">
                  <td colSpan={isSelectMode ? 10 : 9} className="px-6 py-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Items per page selector */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Zeige
                        </span>
                        <div className="relative" style={{ zIndex: showModal || showConfirmation || showGroupsModal || showPartnerTeamsWizard ? 1 : 100, pointerEvents: showModal || showConfirmation || showGroupsModal || showPartnerTeamsWizard ? 'none' : 'auto' }}>
                          <GenericSelectTailwind
                            value={itemsPerPage.toString()}
                            onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                            options={[
                              { value: "10", label: "10" },
                              { value: "20", label: "20" },
                              { value: "50", label: "50" },
                              { value: "100", label: "100" },
                              { value: "all", label: "Alle" }
                            ]}
                            placeholder="10"
                            openUpward={true}
                            whiteBackground={true}
                            disabled={showModal || showConfirmation || showGroupsModal || showPartnerTeamsWizard}
                          />
                        </div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Einträge pro Seite
                        </span>
                      </div>

                      {/* Pagination Info and Controls */}
                      <div className="flex items-center gap-4">
                        {/* Info Text */}
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Teilnehmern
                        </span>

                        {/* Scroll to Top Button */}
                        <button
                          onClick={scrollToTop}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                          title="Nach oben scrollen"
                        >
                          <i className="fas fa-arrow-up text-xs"></i>
                        </button>

                        {/* Pagination Buttons */}
                        {itemsPerPage !== "all" && totalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-left text-xs"></i>
                            </button>

                            {/* Page Numbers */}
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${
                                    currentPage === pageNum
                                      ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}

                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-right text-xs"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredParticipants.length === 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-16 px-8">
            <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-users text-slate-400 dark:text-slate-500 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1.5">Keine Teilnehmer gefunden</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {searchTerm || selectedFilter !== "all" || selectedAgeGroup !== "all" || selectedNationality !== "all" || selectedGender !== "all"
                ? "Versuche die Filter oder den Suchbegriff anzupassen."
                : "Füge den ersten Teilnehmer hinzu, um zu beginnen."
              }
            </p>
            {(!searchTerm && selectedFilter === "all" && selectedAgeGroup === "all" && selectedNationality === "all" && selectedGender === "all") && (
              <button
                onClick={handleAddParticipant}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl hover:bg-primary-dark dark:hover:bg-primary transition-colors shadow-sm"
              >
                <i className="fas fa-plus text-xs"></i>
                Ersten Teilnehmer hinzufügen
              </button>
            )}
          </div>
        )}

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />

      {/* Modal für Teilnehmer hinzufügen/bearbeiten */}
      <AdminModalTailwind
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingParticipant 
          ? t("admin.participants.editTitle", "Teilnehmer bearbeiten") 
          : t("admin.participants.addTitle", "Neuen Teilnehmer hinzufügen")
        }
        subtitle={editingParticipant 
          ? t("admin.participants.editSubtitle", "Bearbeiten Sie die Daten des Teilnehmers.") 
          : t("admin.participants.addSubtitle", "Fügen Sie einen neuen Teilnehmer zur Veranstaltung hinzu.")
        }
        onSave={() => participantFormRef.current?.handleSubmit()}
        onCancel={handleCloseModal}
        saveButtonText={editingParticipant 
          ? t("admin.participants.update", "Aktualisieren") 
          : t("admin.participants.save", "Hinzufügen")
        }
        cancelButtonText={t("admin.cancel", "Abbrechen")}
        isSaving={isSaving}
        isSaveDisabled={!isFormValid}
      >
        <ParticipantFormTailwind
          ref={participantFormRef}
          participant={editingParticipant}
          onSave={handleSaveParticipant}
          onCancel={handleCloseModal}
          isSaving={isSaving}
        />
      </AdminModalTailwind>

      {/* Bestätigungsmodal für Löschen */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmDelete}
        title={participantToDelete?.ids ? "Teilnehmer löschen" : "Teilnehmer löschen"}
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message={participantToDelete?.ids 
          ? `Möchten Sie ${participantToDelete.ids.length} ausgewählte Teilnehmer wirklich löschen?`
          : `Möchten Sie den Teilnehmer "${participantToDelete?.name}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        layout="inline"
      />

      {/* Gruppen-Modal */}
      {showGroupsModal && (
        <AdminModalTailwind
          isOpen={showGroupsModal}
          onClose={handleCloseGroupsModal}
          title="Gruppen des Teilnehmers"
          subtitle={selectedParticipantGroups.length > 0 ? `${selectedParticipantGroups.length} Gruppe${selectedParticipantGroups.length !== 1 ? 'n' : ''} gefunden` : ""}
        >
              {groupsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 dark:border-slate-400"></div>
                  <span className="ml-3 text-slate-600 dark:text-slate-400">Lade Gruppen...</span>
                </div>
              ) : selectedParticipantGroups.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-users text-slate-400 dark:text-slate-500 text-xl"></i>
                  </div>
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Gruppen gefunden</h4>
                  <p className="text-slate-500 dark:text-slate-400">Dieser Teilnehmer ist noch keiner Gruppe zugewiesen.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {selectedParticipantGroups.map((group) => (
                    <div key={group.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                      {/* Gruppen-Header */}
                      <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-700 border-l-4 border-l-primary dark:border-l-primary-light rounded-tl-xl">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <i className="fas fa-layer-group text-slate-400 dark:text-slate-500 text-xs"></i>
                            {group.name}
                          </h3>
                          <div className="ml-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                              group.status === 'Abgeschlossen' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-700/30 dark:border-emerald-300/30' :
                              group.status === 'Aktiv' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-700/30 dark:border-blue-300/30' :
                              group.status === 'Vorbereitung' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-700/30 dark:border-orange-300/30' :
                              'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-700/30 dark:border-yellow-300/30'
                            }`}>
                              <i className={`fas ${
                                group.status === 'Aktiv' ? 'fa-play' : 
                                group.status === 'Ausstehend' ? 'fa-clock' : 
                                group.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                'fa-check'
                              } mr-1.5 text-[10px]`}></i>
                              {group.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`p-6 overflow-visible ${!group.results || group.results.length === 0 ? 'rounded-b-xl' : ''}`}>
                        
                        {/* Zeitplan (ersetzt die Badges) - Volle Breite */}
                        {(() => {
                          const scheduleEvent = group.scheduleEvent || group;
                          const hasSchedule = scheduleEvent.startTime || scheduleEvent.endTime;
                          
                          if (!hasSchedule) {
                            return (
                              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 p-4 w-full">
                                <p className="text-sm text-slate-500 dark:text-slate-400">Kein Zeitplan verfügbar</p>
                              </div>
                            );
                          }

                          // Finde die Startnummer des Teilnehmers in dieser Gruppe
                          let participantStartNumber = null;
                          
                          // Versuche verschiedene Datenstrukturen
                          if (group.participants && Array.isArray(group.participants)) {
                            const participant = group.participants.find(p => 
                              p.id === selectedParticipantForGroups?.id || 
                              p.participantId === selectedParticipantForGroups?.id ||
                              (p.participant && (p.participant.id === selectedParticipantForGroups?.id || p.participant.participantId === selectedParticipantForGroups?.id))
                            );
                            participantStartNumber = participant?.startNumber || participant?.start_number;
                          }
                          
                          if (!participantStartNumber && group.participantGroups && Array.isArray(group.participantGroups)) {
                            const participantGroup = group.participantGroups.find(pg => 
                              pg.participantId === selectedParticipantForGroups?.id ||
                              (pg.participant && (pg.participant.id === selectedParticipantForGroups?.id || pg.participant.participantId === selectedParticipantForGroups?.id))
                            );
                            participantStartNumber = participantGroup?.startNumber || participantGroup?.start_number;
                          }
                          
                          // Fallback: Direkt im group-Objekt
                          if (!participantStartNumber && group.startNumber) {
                            participantStartNumber = group.startNumber;
                          }

                          // Berechne Dauer in Minuten
                          const startTime = new Date(scheduleEvent.startTime);
                          const endTime = new Date(scheduleEvent.endTime);
                          const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));

                          // Bestimme welche Minuten markiert werden sollen
                          const twoParallel = group.twoParallel || group.two_parallel || group.isParallel || false;
                          let startMin = null;
                          let endMin = null;
                          
                          if (participantStartNumber) {
                            if (twoParallel) {
                              // Bei Parallel: Starter 1-2 bekommen Min 1-2, Starter 3-4 bekommen Min 3-4, etc.
                              const slotIndex = Math.ceil(participantStartNumber / 2) - 1;
                              startMin = slotIndex * 2 + 1;
                              endMin = Math.min((slotIndex + 1) * 2, durationMinutes);
                            } else {
                              // Bei Alleine: Starter 1 bekommt Min 1-2, Starter 2 bekommt Min 3-4, etc.
                              startMin = (participantStartNumber - 1) * 2 + 1;
                              endMin = Math.min(participantStartNumber * 2, durationMinutes);
                            }
                          }

                          // Berechne aktuellen Fortschritt (falls Event läuft)
                          const now = new Date();
                          let currentProgress = 0;
                          if (now >= startTime && now <= endTime) {
                            const elapsedMinutes = Math.round((now - startTime) / (1000 * 60));
                            currentProgress = Math.min((elapsedMinutes / durationMinutes) * 100, 100);
                          } else if (now > endTime) {
                            currentProgress = 100;
                          }

                          // Formatiere Datum mit führenden Nullen
                          const formatDate = (date) => {
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}.${month}.${year}`;
                          };

                          // Berechne Position und Breite des zusammenhängenden Balkens
                          let activeBarLeft = 0;
                          let activeBarWidth = 0;
                          if (startMin !== null && endMin !== null && durationMinutes > 0) {
                            activeBarLeft = ((startMin - 1) / durationMinutes) * 100;
                            activeBarWidth = ((endMin - startMin + 1) / durationMinutes) * 100;
                          }

                          return (
                            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 w-full">
                              {/* Header */}
                              <div className="flex items-center justify-between mb-4">
                                <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">ZEITPLAN</h5>
                                <div className="flex items-center gap-4 text-sm">
                                  {scheduleEvent.startTime && (
                                    <div className="flex items-center gap-2">
                                      <i className="fas fa-calendar text-slate-400 dark:text-slate-500"></i>
                                      <span className="text-slate-600 dark:text-slate-300">
                                        {formatDate(new Date(scheduleEvent.startTime))}
                                      </span>
                                    </div>
                                  )}
                                  {(scheduleEvent.venue || group.venue) && (
                                    <div className="flex items-center gap-2">
                                      <i className="fas fa-map-marker-alt text-slate-400 dark:text-slate-500"></i>
                                      <span className="text-slate-600 dark:text-slate-300">{scheduleEvent.venue || group.venue}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <i className={`fas ${twoParallel ? 'fa-users' : 'fa-user'} text-slate-400 dark:text-slate-500`}></i>
                                    <span className="text-slate-600 dark:text-slate-300">
                                      {twoParallel ? 'Parallel' : 'Alleine'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <div className="mb-4">
                                <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden relative">
                                  {/* Aktueller Fortschritt (grau) - darunter */}
                                  {currentProgress > 0 && (
                                    <div
                                      className="absolute h-2 bg-slate-400 dark:bg-slate-500 rounded-full transition-all duration-300 z-0"
                                      style={{ width: `${currentProgress}%` }}
                                    />
                                  )}
                                  {/* Aktive Minuten für diesen Teilnehmer (blau) - zusammenhängender Balken */}
                                  {activeBarWidth > 0 && (
                                    <div
                                      className="absolute h-2 bg-primary dark:bg-primary-light rounded-full z-10"
                                      style={{ 
                                        left: `${activeBarLeft}%`,
                                        width: `${activeBarWidth}%`
                                      }}
                                    />
                                  )}
                                </div>
                              </div>

                              {/* Zeit und Dauer */}
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 dark:text-slate-400">
                                  {startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-slate-500 dark:text-slate-400">
                                  Dauer: {durationMinutes} Min.
                                </span>
                                <span className="text-slate-500 dark:text-slate-400">
                                  {endTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Gruppen-Details */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Teilnehmerzahl</div>
                            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                              {group.participantCount || group.participants?.length || 0}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Startnummer</div>
                            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                              #{group.startNumber || '-'}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">Platzierung</div>
                            <div className={`text-lg font-semibold ${
                              group.rank === 1 ? 'text-yellow-700 dark:text-yellow-300' :
                              group.rank === 2 ? 'text-slate-600 dark:text-slate-400' :
                              group.rank === 3 ? 'text-orange-700 dark:text-orange-300' :
                              'text-slate-900 dark:text-slate-100'
                            }`}>
                              {group.rank ? `${group.rank}.` : '-'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Ergebnisse (falls vorhanden) */}
                      {group.results && group.results.length > 0 && (
                        <div className="border-t border-slate-200 dark:border-slate-700">
                          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                              <i className="fas fa-trophy text-[9px]"></i>
                              Ergebnisse
                            </span>
                          </div>
                          <div className="p-6 overflow-visible rounded-b-xl">
                            <div className="overflow-x-auto rounded-b-xl">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-100 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">WR 1</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">WR 2</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">WR 3</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">WR 4</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">WR 5</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Penalty</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Punkte</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Rang</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-800">
                                  {group.results.map((result, index) => {
                                    const isLast = index === group.results.length - 1;
                                    return (
                                      <tr key={index} className={
                                        result.rank === 1 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                                        result.rank === 2 ? 'bg-slate-50 dark:bg-slate-700/30' :
                                        result.rank === 3 ? 'bg-orange-50 dark:bg-orange-900/20' :
                                        ''
                                      }>
                                        <td className={`px-4 py-3 text-slate-900 dark:text-slate-100 ${isLast ? 'rounded-bl-xl' : ''}`}>{result.judgeScore1 || '-'}</td>
                                        <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{result.judgeScore2 || '-'}</td>
                                        <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{result.judgeScore3 || '-'}</td>
                                        <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{result.judgeScore4 || '-'}</td>
                                        <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{result.judgeScore5 || '-'}</td>
                                        <td className="px-4 py-3">
                                          {result.penalty ? (
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                              result.penalty > 0 
                                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-700/30 dark:border-red-300/30'
                                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-700/30 dark:border-green-300/30'
                                            }`}>
                                              {result.penalty > 0 ? `+${result.penalty}` : result.penalty}
                                            </span>
                                          ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{result.finalScore || '-'}</td>
                                        <td className={`px-4 py-3 ${isLast ? 'rounded-br-xl' : ''}`}>
                                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border ${
                                            result.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-700/30 dark:border-yellow-300/30' :
                                              result.rank === 2 ? 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30' :
                                              result.rank === 3 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-700/30 dark:border-orange-300/30' :
                                              'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30'
                                          }`}>
                                            {result.rank ? `${result.rank}.` : '-'}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  ))}
                </div>
              )}
        </AdminModalTailwind>
      )}

      {/* Partner Teams Wizard */}
      <PartnerTeamsWizard
        isOpen={showPartnerTeamsWizard}
        onClose={() => setShowPartnerTeamsWizard(false)}
        onSuccess={() => {
          addToast('success', 'Partner-Teams erfolgreich erstellt');
          loadData();
        }}
      />
</div>
    </main>
  );
}

export default ParticipantsTailwind;
